// SheetJS 를 만지는 유일한 자리. 모듈은 호출하는 쪽이 넘긴다 — 동적 import 로 초기 번들에서 뺀다.

import type * as XLSX from "xlsx";

import { MAX_IMPORT_BYTES, MAX_IMPORT_ROWS } from "./types";

type XlsxModule = typeof XLSX;

/** 열 상한 — 서식만 번진 먼 열까지 읽지 않는다. */
const MAX_COLS = 60;
/** 줄 상한 — 거대 시트는 파싱 전에 자른다(머리글·집계 줄 여유 500). */
const MAX_SHEET_ROWS = MAX_IMPORT_ROWS + 500;
/** 자른 끝 이만큼 안에 값이 있어야 「잘렸다」 로 본다 — 서식만 번진 범위는 잘림이 아니다. */
const CUT_MARGIN = 50;

/** 사람에게 그대로 보여 줄 가져오기 오류. */
export class ImportFileError extends Error {}

// 숫자 칸은 표시값이 아니라 값 그대로 — 「0 자리」 서식이 1.5 를 2 로 보이게 해도 1.5 를 읽는다.
const numberText = (v: number): string => {
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 1e-6) return String(rounded);
  return String(Number(v.toFixed(6)));
};

const cellText = (xlsx: XlsxModule, cell: XLSX.CellObject | undefined): string => {
  if (!cell) return "";
  if (cell.t === "n" && typeof cell.v === "number") {
    // 날짜 서식이면 일련번호(45905)가 아니라 보이는 글자(「9/1」「3-5」)로.
    const isDate = typeof cell.z === "string" && xlsx.SSF.is_date(cell.z);
    return isDate && cell.w !== undefined ? cell.w : numberText(cell.v);
  }
  if (cell.t === "e") return cell.w ?? "";
  if (cell.w !== undefined) return cell.w;
  return cell.v === undefined || cell.v === null ? "" : String(cell.v);
};

/** 시트 → 문자열 격자와 줄마다의 엑셀 행 번호(1부터). 병합은 첫 칸 값(세로 병합은 아래로 채움), 수식은 계산값, 빈 줄은 뺀다. */
export const sheetToRows = (
  xlsx: XlsxModule,
  ws: XLSX.WorkSheet
): { grid: string[][]; rowNumbers: number[] } => {
  const cells = new Map<string, string>();
  let maxR = -1;
  let maxC = -1;
  // `!ref` 대신 실제 칸만 돈다 — 서식이 번진 시트는 범위가 백만 줄이다.
  for (const addr of Object.keys(ws)) {
    if (addr.startsWith("!")) continue;
    const { r, c } = xlsx.utils.decode_cell(addr);
    if (c >= MAX_COLS) continue;
    const text = cellText(xlsx, ws[addr] as XLSX.CellObject).trim();
    if (text === "") continue;
    cells.set(`${r}:${c}`, text);
    if (r > maxR) maxR = r;
    if (c > maxC) maxC = c;
  }
  for (const m of ws["!merges"] ?? []) {
    if (m.e.r === m.s.r) continue;
    const first = cells.get(`${m.s.r}:${m.s.c}`);
    if (first === undefined) continue;
    for (let r = m.s.r + 1; r <= m.e.r; r += 1) {
      if (!cells.has(`${r}:${m.s.c}`)) cells.set(`${r}:${m.s.c}`, first);
    }
    if (m.e.r > maxR) maxR = m.e.r;
  }

  const grid: string[][] = [];
  const rowNumbers: number[] = [];
  for (let r = 0; r <= maxR; r += 1) {
    const row: string[] = [];
    let any = false;
    for (let c = 0; c <= maxC; c += 1) {
      const v = cells.get(`${r}:${c}`) ?? "";
      if (v !== "") any = true;
      row.push(v);
    }
    if (any) {
      grid.push(row);
      rowNumbers.push(r + 1);
    }
  }
  return { grid, rowNumbers };
};

// 줄 상한에서 실제 자료가 잘렸나 — `!fullref` 는 서식만 번진 범위에도 붙는다.
const cutOff = (xlsx: XlsxModule, ws: XLSX.WorkSheet): boolean => {
  if (ws["!fullref"] === undefined) return false;
  return Object.keys(ws).some((addr) => {
    if (addr.startsWith("!")) return false;
    const cell = ws[addr] as XLSX.CellObject;
    return cell.v !== undefined && xlsx.utils.decode_cell(addr).r >= MAX_SHEET_ROWS - CUT_MARGIN;
  });
};

// 엑셀이 저장한 CSV 는 대개 CP949 다 — UTF-8 로 안 읽히면 EUC-KR 로 푼다.
const decodeCsv = (bytes: Uint8Array): string => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes).replace(/^﻿/, "");
  } catch {
    return new TextDecoder("euc-kr").decode(bytes);
  }
};

const startsWith = (bytes: Uint8Array, sig: number[]): boolean =>
  sig.every((b, i) => bytes[i] === b);
const ZIP_SIG = [0x50, 0x4b];
const CFB_SIG = [0xd0, 0xcf, 0x11, 0xe0];

// SheetJS 는 아무 바이트나 글자 시트로 읽어 버린다 — 확장자에 맞는 머리 바이트인지 먼저 본다.
const looksLikeExcel = (bytes: Uint8Array, fileName: string): boolean => {
  if (/\.(xlsx|xlsm)$/i.test(fileName)) {
    return startsWith(bytes, ZIP_SIG) || startsWith(bytes, CFB_SIG);
  }
  if (/\.xls$/i.test(fileName)) {
    const head = new TextDecoder("latin1").decode(bytes.subarray(0, 64)).trimStart();
    return startsWith(bytes, CFB_SIG) || startsWith(bytes, ZIP_SIG) || head.startsWith("<");
  }
  return true;
};

export interface LoadedWorkbook {
  sheetNames: string[];
  /** 시트 이름 → 격자. */
  grids: Record<string, string[][]>;
  /** 시트 이름 → 격자 줄마다의 엑셀 행 번호(빈 행을 센 1부터). */
  rowNumbers: Record<string, number[]>;
  /** 줄 상한에서 잘린 시트. */
  truncated: string[];
}

/** 파일 바이트를 시트별 격자로. 읽을 수 없으면 `ImportFileError`. */
export const readWorkbook = (
  xlsx: XlsxModule,
  bytes: Uint8Array,
  fileName: string
): LoadedWorkbook => {
  if (bytes.byteLength > MAX_IMPORT_BYTES) {
    throw new ImportFileError("파일이 너무 큽니다 — 10MB 이하로 올려 주세요");
  }
  if (bytes.byteLength === 0) throw new ImportFileError("빈 파일입니다");

  const broken = "파일을 읽지 못했습니다 — 깨졌거나 엑셀 파일이 아닙니다(.xlsx·.xls·.csv)";
  if (!looksLikeExcel(bytes, fileName)) throw new ImportFileError(broken);

  let wb: XLSX.WorkBook;
  // CSV 는 `!fullref` 가 안 남아 줄 수로 잘림을 판정한다.
  let csvCut = false;
  try {
    if (/\.csv$/i.test(fileName)) {
      const text = decodeCsv(bytes);
      csvCut = text.split(/\r?\n/).filter((l) => l.trim() !== "").length > MAX_SHEET_ROWS;
      wb = xlsx.read(text, { type: "string", raw: false, cellNF: true, sheetRows: MAX_SHEET_ROWS });
    } else {
      wb = xlsx.read(bytes, {
        type: "array",
        cellFormula: false,
        cellHTML: false,
        cellNF: true,
        sheetRows: MAX_SHEET_ROWS,
      });
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "";
    if (/password|encrypt/i.test(message)) {
      throw new ImportFileError(
        "암호가 걸린 파일입니다 — 엑셀에서 암호를 풀고 다시 저장해 올려 주세요"
      );
    }
    throw new ImportFileError(broken);
  }

  const grids: Record<string, string[][]> = {};
  const rowNumbers: Record<string, number[]> = {};
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    const read = ws ? sheetToRows(xlsx, ws) : { grid: [], rowNumbers: [] };
    grids[name] = read.grid;
    rowNumbers[name] = read.rowNumbers;
  }
  const sheetNames = wb.SheetNames.filter((n) => grids[n].length > 0);
  if (sheetNames.length === 0) throw new ImportFileError("내용이 있는 시트가 없습니다");
  const truncated = sheetNames.filter((n) => csvCut || cutOff(xlsx, wb.Sheets[n]));
  return { sheetNames, grids, rowNumbers, truncated };
};
